/** biome-ignore-all lint/style/useNamingConvention: allow snake case */
import {useCallback, useState} from 'react';
import {toastAlert} from '~library/atoms/Toast';
import {Typography} from '~library/atoms/Typography';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {AlertLineIcon} from '~library/svg/AlertLineIcon';
import {CheckmarkIcon} from '~library/svg/CheckmarkIcon';

const SESSION_KEY = 'evite_resend_timestamps';
const TEN_MINUTES_MS = 10 * 60 * 1000;

interface ResendInvitationParams {
  eventId: number;
  id: string;
  name: string;
}

interface ResendInvitationResult {
  resendInvitation: (params: ResendInvitationParams) => Promise<boolean>;
  isResending: boolean;
  resendingId: string | null;
  resendingGuestName: string | null;
  canResendToGuest: (id: string) => boolean;
}

const getResendTimestamps = (): Record<string, number> => {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    evite.error('Failed to load resend timestamps', error);
    return {};
  }
};

const recordResendTimestamp = (id: string): void => {
  try {
    const timestamps = getResendTimestamps();
    timestamps[id] = Date.now();
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(timestamps));
  } catch (error) {
    evite.error('Failed to record resend timestamp', error);
  }
};

const canResendToGuest = (id: string): boolean => {
  const timestamps = getResendTimestamps();
  const lastResent = timestamps[id];

  if (!lastResent) return true;

  const timeSinceResend = Date.now() - lastResent;
  return timeSinceResend >= TEN_MINUTES_MS;
};

const showSuccessToast = (isDesktop: boolean): void => {
  toastAlert(
    <Typography as="span" variant="paragraph2">
      <Typography as="span" variant="paragraph2" semibold>
        Invitation resent
      </Typography>
    </Typography>,
    {
      type: 'success',
      icon: <CheckmarkIcon ratio={0.65} color="emeraldWebText" />,
      position: isDesktop ? 'bottom-right' : 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      closeButton: false,
    }
  );
};

const showRateLimitToast = (isDesktop: boolean): void => {
  toastAlert(
    <Typography as="span" variant="paragraph2">
      <Typography as="span" variant="paragraph2" semibold>
        Invitation recently resent
      </Typography>
      <br />
      Give your guest a little time to check their inbox.
    </Typography>,
    {
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} />,
      position: isDesktop ? 'bottom-right' : 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      className: 'toast--light',
      closeButton: false,
    }
  );
};

const showErrorToast = (isDesktop: boolean): void => {
  toastAlert(
    <Typography as="span" variant="paragraph2">
      <Typography as="span" variant="paragraph2" semibold>
        Could not resend Invitation
      </Typography>
      <br />
      Please try again.
    </Typography>,
    {
      type: 'error',
      icon: <AlertLineIcon ratio={0.65} />,
      position: isDesktop ? 'bottom-right' : 'top-right',
      autoClose: 5000,
      hideProgressBar: true,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    }
  );
};

export const useResendInvitation = (): ResendInvitationResult => {
  const [isResending, setIsResending] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [resendingGuestName, setResendingGuestName] = useState<string | null>(null);

  const isDesktop = useIsLargishAndUp();

  const resendInvitation = useCallback(
    async ({eventId, id, name}: ResendInvitationParams): Promise<boolean> => {
      if (!canResendToGuest(id)) {
        showRateLimitToast(isDesktop);
        return false;
      }

      setIsResending(true);
      setResendingId(id);
      setResendingGuestName(name);

      try {
        const response = await evite.fetch(`/services/event/v1/${eventId}/guests/${id}/resend`, {
          method: 'POST',
          cache: 'no-cache',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': evite.cookie('csrftoken'),
            Accept: 'application/json',
          },
        });

        const data = await response.json();

        if (response.ok) {
          recordResendTimestamp(id);
          showSuccessToast(isDesktop);
          return true;
        }

        if (response.status === 429) {
          showRateLimitToast(isDesktop);
          return false;
        }

        evite.error('Failed to resend invitation', data.message || 'Unknown error');
        showErrorToast(isDesktop);
        return false;
      } catch (error) {
        evite.error('Failed to resend invitation', error);
        showErrorToast(isDesktop);
        return false;
      } finally {
        setIsResending(false);
        setResendingId(null);
        setResendingGuestName(null);
      }
    },
    [isDesktop]
  );

  return {
    resendInvitation,
    isResending,
    resendingId,
    resendingGuestName,
    canResendToGuest,
  };
};
